import type { HomeDistribution } from "@unbox/api";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { ASK_TITLE, type AskRequest, askPromptsFor } from "../lib/ask-prompts";
import { bucketLabel, type DistributionRange, noteRangePhrase, waitingLabel } from "../lib/ranges";
import { type ChartSeries, DistributionBarChart } from "./distribution-bar-chart";
import { StatCard } from "./stat-card";

const CHANNEL_SERIES: ChartSeries[] = [
  { key: "video", name: "Video", color: "#2f2245" },
  { key: "productCard", name: "Product card", color: "#6843b5" },
  { key: "live", name: "Live", color: "#f79009" },
];

const CREATOR_SERIES: ChartSeries[] = [
  { key: "repeatCreators", name: "Repeat creators", color: "#2e90fa" },
  { key: "newCreators", name: "New creators", color: "#17b26a" },
];

const VIDEO_SERIES: ChartSeries[] = [
  { key: "repeatVideos", name: "From repeat creators", color: "#2e90fa" },
  { key: "newVideos", name: "From new creators", color: "#17b26a" },
];

const SEVERITY_ORDER = [
  { severity: "critical", label: "Critical", color: "#d42b2b" },
  { severity: "high", label: "High", color: "#f79009" },
  { severity: "medium", label: "Medium", color: "#eaaa08" },
  { severity: "low", label: "Low", color: "#9ca3af" },
] as const;

type CardProps = {
  data: HomeDistribution;
  range: DistributionRange;
  onAsk: (request: AskRequest) => void;
  onFooter: () => void;
};

const legendFrom = (series: ChartSeries[]) => series.map(({ name, color }) => ({ name, color }));

export function ChannelPerformanceCard({
  data,
  range,
  onAsk,
  onFooter,
  formatGmv,
}: CardProps & { formatGmv: (value: number) => string }) {
  const { gmv, buckets } = data.channelPerformance;
  const phrase = noteRangePhrase(range);

  return (
    <StatCard
      label="Channel performance"
      tint="#fbe9d7"
      accent="#b54708"
      value={formatGmv(gmv.current)}
      change={gmv.change}
      note={`Total shop GMV in the ${phrase}, against ${formatGmv(gmv.prior)} in the ${phrase} before.`}
      onAsk={() => onAsk({ suggestions: askPromptsFor("channel", range), title: ASK_TITLE.channel })}
      legend={legendFrom(CHANNEL_SERIES)}
      footer={{ label: "Channel breakdown", onClick: onFooter }}
    >
      <DistributionBarChart
        data={buckets.map((bucket) => ({
          label: bucketLabel(data.bucket, bucket.periodStart, bucket.periodEnd),
          video: bucket.video,
          productCard: bucket.productCard,
          live: bucket.live,
        }))}
        series={CHANNEL_SERIES}
        totalUnit="total GMV"
        formatValue={formatGmv}
      />
    </StatCard>
  );
}

export function BrandSafetyCard({ data, range, onAsk, onFooter }: CardProps) {
  const { brandSafety } = data;
  const phrase = noteRangePhrase(range);
  const severityTotals = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const { tier, count } of brandSafety.severityCounts) {
    severityTotals[tierToSeverity(tier ?? 4)] += count;
  }

  return (
    <StatCard
      label="Brand safety"
      tint="#ddf2e6"
      accent="#087443"
      value={brandSafety.pendingReview.toLocaleString()}
      valueSuffix="videos need action"
      note={`${brandSafety.risksFound.current.toLocaleString()} risks found in ${brandSafety.videosAnalysed.current.toLocaleString()} videos analysed in the ${phrase} · ${brandSafety.risksActioned.current.toLocaleString()} already actioned.`}
      onAsk={() => onAsk({ suggestions: askPromptsFor("safety", range), title: ASK_TITLE.safety })}
      footer={{ label: "Review videos", onClick: onFooter }}
    >
      {brandSafety.pendingReview === 0 ? (
        <p className="text-[14px] leading-5 text-[#21133a]/70">
          Nothing waiting — the queue cleared inside this window.
        </p>
      ) : (
        <>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {SEVERITY_ORDER.map((entry) => (
              <li key={entry.severity} className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: entry.color }}
                  className="size-2.5 shrink-0 rounded-[3px]"
                />
                <span className="min-w-0 flex-1 text-[14px] leading-5 text-[#21133a]/70">{entry.label}</span>
                <span className="shrink-0 text-[14px] leading-5 font-semibold text-[#21133a] tabular-nums">
                  {severityTotals[entry.severity]}
                </span>
              </li>
            ))}
          </ul>
          {brandSafety.oldestPendingReviewAt && (
            <p className="flex flex-wrap items-baseline gap-1.5 text-[14px] leading-5 text-[#21133a]/70">
              Oldest has been waiting
              <span className="font-semibold text-[#21133a]">
                {waitingLabel(brandSafety.oldestPendingReviewAt, new Date())}
              </span>
            </p>
          )}
        </>
      )}
    </StatCard>
  );
}

export function ActiveCreatorsCard({ data, range, onAsk, onFooter }: CardProps) {
  const { buckets, creators } = data.creatorActivity;

  return (
    <StatCard
      label="Active Creators"
      tint="#dfedfa"
      accent="#175cd3"
      value={creators.total.current.toLocaleString()}
      change={creators.total.change}
      note={`Creators who posted at least one video in the ${noteRangePhrase(range)}`}
      onAsk={() => onAsk({ suggestions: askPromptsFor("creators", range), title: ASK_TITLE.creators })}
      legend={legendFrom(CREATOR_SERIES)}
      footer={{ label: "See creators", onClick: onFooter }}
    >
      <DistributionBarChart
        data={buckets.map((bucket) => ({
          label: bucketLabel(data.bucket, bucket.periodStart, bucket.periodEnd),
          repeatCreators: bucket.repeatCreators,
          newCreators: bucket.newCreators,
        }))}
        series={CREATOR_SERIES}
        totalUnit="creators posted"
        formatValue={(value) => value.toLocaleString()}
      />
    </StatCard>
  );
}

export function VideosPostedCard({ data, range, onAsk, onFooter }: CardProps) {
  const { buckets, videos } = data.creatorActivity;

  return (
    <StatCard
      label="Videos posted"
      tint="#dfedfa"
      accent="#175cd3"
      value={videos.total.current.toLocaleString()}
      change={videos.total.change}
      note={`Videos posted in the ${noteRangePhrase(range)}`}
      onAsk={() => onAsk({ suggestions: askPromptsFor("videos", range), title: ASK_TITLE.videos })}
      legend={legendFrom(VIDEO_SERIES)}
      footer={{ label: "See videos", onClick: onFooter }}
    >
      <DistributionBarChart
        data={buckets.map((bucket) => ({
          label: bucketLabel(data.bucket, bucket.periodStart, bucket.periodEnd),
          repeatVideos: bucket.repeatVideos,
          newVideos: bucket.newVideos,
        }))}
        series={VIDEO_SERIES}
        totalUnit="videos posted"
        formatValue={(value) => value.toLocaleString()}
      />
    </StatCard>
  );
}
